import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Search, Plus, Edit, Trash2, ToggleLeft, ToggleRight,
  Image as ImageIcon, X, Tag, FolderPlus, Folder,
  Truck, MapPin, Eye, EyeOff, ArrowUp, ArrowDown,
  Shield,
  Upload, ChevronLeft, ChevronRight
} from 'lucide-react'
import { giftsApi, giftCategoriesApi, hospitalsApi, uploadApi } from '../../lib/api'
import RichEditor from '../../components/RichEditorLazy'

interface GiftItem {
  id: string
  name: string
  points_required: number
  stock: number
  description?: string
  image?: string
  image_url?: string
  category?: string
  is_active: boolean
  exchange_limit?: number
  sort_order?: number
  delivery_type?: string
  created_at?: string
  hospital_ids?: string[]
  hospital_names?: string[]
  price?: number
  creator_hospital_id?: string
}

interface Category {
  id: string
  name: string
  sort_order: number
  count?: number
  hidden?: boolean
  created_at?: string
}

const Gifts = () => {
  const [gifts, setGifts] = useState<GiftItem[]>([])
  const [total, setTotal] = useState(0)
  const [allTotal, setAllTotal] = useState(0)  // 全部礼品总数
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 8
  const totalPages = Math.ceil(total / pageSize)

  // 当前是否为机构负责人（从 auth_user 读取）
  const rawAuthUser = localStorage.getItem('auth_user')
  const authUser = rawAuthUser ? JSON.parse(rawAuthUser) : null
  const isSupervisor = authUser?.role === 'medical' && authUser?.medicalRole === 'supervisor'
  const isAdmin = authUser?.role === 'admin'
  // 机构负责人默认绑定自己机构
  const supervisorHospitalId = authUser?.hospitalId || null

  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState<'add' | 'edit'>('add')
  const [selectedGift, setSelectedGift] = useState<GiftItem | null>(null)
  const [formLoading, setFormLoading] = useState(false)
  const [deletingGift, setDeletingGift] = useState<GiftItem | null>(null)

  const [formData, setFormData] = useState({
    name: '', points_required: '', stock: '', description: '',
    image: '', exchange_limit: '', sort_order: '', delivery_type: 'express' as string,
    category: '', hospital_ids: [] as string[], price: ''
  })
  const [isActive, setIsActive] = useState(true)
  const [hospitals, setHospitals] = useState<{ id: string; name: string }[]>([])

  // 分类
  const [showCategoryModal, setShowCategoryModal] = useState(false)
  const [categories, setCategories] = useState<Category[]>([])
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [newCategory, setNewCategory] = useState('')
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)

  // 弹窗内图片预览
  const [imagePreview, setImagePreview] = useState('')
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [uploadingImage, setUploadingImage] = useState(false)
  const imageInputRef = useRef<HTMLInputElement>(null)

  // 富文本描述
  const [descContent, setDescContent] = useState('')

  // 弹窗打开时加载机构列表
  useEffect(() => {
    if (showModal) {
      hospitalsApi.list({ is_active: true }).then((res: any) => {
        setHospitals((res.hospitals || res || []).map((h: any) => ({ id: h.id, name: h.name })))
      }).catch(() => {})
    }
  }, [showModal])

  const fetchGifts = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params: Record<string, unknown> = { page: currentPage, page_size: pageSize }
      if (searchTerm) params.keyword = searchTerm
      if (categoryFilter !== 'all') params.category = categoryFilter
      const res = await giftsApi.list(params) as { total: number; gifts?: any[]; items?: any[] }
      const rawItems = res.gifts || res.items || []
      const items = rawItems.map((g: any) => ({
        ...g,
        points_required: g.points ?? g.points_required ?? 0,
        is_active: Boolean(g.enabled),
        image: g.image || g.image_url || '',
        exchange_limit: g.exchange_limit ?? 0,
      }))
      setGifts(items)
      setTotal(res.total || items.length)
      // 当筛选项为全部时，res.total 就是全部礼品总数
      if (categoryFilter === 'all') setAllTotal(res.total || items.length)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [currentPage, searchTerm, categoryFilter])

  // 加载分类列表
  const fetchCategories = useCallback(async () => {
    try {
      const res = await giftCategoriesApi.list() as { categories: Category[] }
      setCategories(res.categories || [])
    } catch {}
  }, [])

  useEffect(() => { fetchGifts() }, [fetchGifts])
  useEffect(() => { fetchCategories() }, [fetchCategories])

  useEffect(() => {
    const t = setTimeout(() => setCurrentPage(1), 400)
    return () => clearTimeout(t)
  }, [searchTerm, categoryFilter])

  // 图片上传处理
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { alert('请选择图片文件'); return }
    if (file.size > 5 * 1024 * 1024) { alert('图片大小不能超过5MB'); return }
    const reader = new FileReader()
    reader.onload = (ev) => {
      setImagePreview(ev.target?.result as string)
      setImageFile(file)
      // 自动上传
      handleAutoUpload(file)
    }
    reader.readAsDataURL(file)
  }

  const handleAutoUpload = async (file: File) => {
    setUploadingImage(true)
    try {
      const url = await uploadApi.uploadImage(file)
      setFormData(d => ({ ...d, image: url }))
      setImagePreview(url)
      setImageFile(null)
    } catch (err) {
      alert((err as Error).message)
      setImagePreview('')
      setImageFile(null)
    } finally {
      setUploadingImage(false)
    }
  }

  const openAdd = () => {
    setModalType('add')
    // supervisor 添加时自动绑定本机构
    const defaultHospitalIds = isSupervisor && supervisorHospitalId ? [supervisorHospitalId] : []
    setFormData({ name: '', points_required: '', stock: '', description: '', image: '', exchange_limit: '', sort_order: '0', delivery_type: 'express', category: '', hospital_ids: defaultHospitalIds, price: '' })
    setIsActive(true)
    setImagePreview('')
    setImageFile(null)
    setDescContent('')
    setShowModal(true)
  }

  const openEdit = (gift: GiftItem) => {
    // 机构负责人不能编辑总后台创建的礼品
    if (isSupervisor && !gift.creator_hospital_id) {
      alert('总后台创建的礼品仅限查看，不可编辑')
      return
    }
    setSelectedGift(gift)
    setModalType('edit')
    setFormData({
      name: gift.name,
      points_required: String(gift.points_required),
      stock: String(gift.stock),
      description: gift.description || '',
      image: gift.image || gift.image_url || '',
      exchange_limit: String(gift.exchange_limit || 0),
      sort_order: String(gift.sort_order || 0),
      delivery_type: gift.delivery_type || 'express',
      category: gift.category || '',
      hospital_ids: gift.hospital_ids || [],
      price: String(gift.price || 0),
    })
    setIsActive(gift.is_active)
    setImagePreview(gift.image || gift.image_url || '')
    setImageFile(null)
    setDescContent(gift.description || '')
    setShowModal(true)
  }

  const handleSubmit = async () => {
    if (!formData.name) { alert('请输入礼品名称'); return }
    if (!formData.points_required || parseInt(formData.points_required) <= 0) { alert('请输入有效的积分'); return }
    if (!formData.stock || parseInt(formData.stock) < 0) { alert('请输入有效的库存'); return }
    setFormLoading(true)
    try {
      const payload = {
        name: formData.name,
        points: parseInt(formData.points_required),
        stock: parseInt(formData.stock),
        description: descContent,
        image: formData.image,
        category: formData.category || undefined,
        exchange_limit: formData.exchange_limit ? parseInt(formData.exchange_limit) : undefined,
        sort_order: formData.sort_order ? parseInt(formData.sort_order) : undefined,
        delivery_type: formData.delivery_type,
        enabled: isActive ? 1 : 0,
        hospital_ids: formData.hospital_ids,
        price: formData.price ? parseInt(formData.price) : undefined,
      }
      if (modalType === 'add') {
        await giftsApi.create(payload as any)
      } else if (selectedGift) {
        await giftsApi.update(selectedGift.id, payload as any)
      }
      setShowModal(false)
      fetchGifts()
    } catch (e) {
      alert((e as Error).message)
    } finally {
      setFormLoading(false)
    }
  }

  const handleToggle = async (gift: GiftItem) => {
    try {
      await giftsApi.toggle(gift.id, !gift.is_active)
      fetchGifts()
    } catch (e) {
      alert((e as Error).message)
    }
  }

  const handleDelete = async () => {
    if (!deletingGift) return
    try {
      await giftsApi.delete(deletingGift.id)
      setDeletingGift(null)
      fetchGifts()
    } catch (e) {
      alert((e as Error).message)
    }
  }

  // 分类 CRUD
  const handleAddCategory = async () => {
    if (!newCategory.trim()) { alert('请输入分类名称'); return }
    try {
      await giftCategoriesApi.create(newCategory.trim(), categories.length + 1)
      setNewCategory('')
      fetchCategories()
    } catch (e) {
      alert((e as Error).message)
    }
  }

  const handleEditCategory = async () => {
    if (!editingCategory || !newCategory.trim()) { alert('请输入分类名称'); return }
    try {
      await giftCategoriesApi.update(editingCategory.id, newCategory.trim(), editingCategory.sort_order)
      setNewCategory('')
      setEditingCategory(null)
      fetchCategories()
    } catch (e) {
      alert((e as Error).message)
    }
  }

  const handleDeleteCategory = async (id: string) => {
    if (!confirm('确定要删除该分类吗？')) return
    try {
      await giftCategoriesApi.remove(id)
      fetchCategories()
      if (categoryFilter === id) setCategoryFilter('all')
    } catch (e) {
      alert((e as Error).message)
    }
  }

  const handleMoveCategory = async (id: string, direction: 'up' | 'down') => {
    const sorted = [...categories].sort((a, b) => b.sort_order - a.sort_order)
    const idx = sorted.findIndex(c => c.id === id)
    const newIdx = direction === 'up' ? idx - 1 : idx + 1
    if (newIdx < 0 || newIdx >= sorted.length) return

    // 交换两项在数组中的位置
    const updated = [...sorted]
    ;[updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]]

    // 按位置顺序重新分配唯一的 sort_order（避免重复值）
    // 显示顺序第0个 -> sort_order=总数-0，第1个 -> sort_order=总数-1，以此类推
    const total = updated.length
    const reordered = updated.map((c, i) => ({ ...c, sort_order: total - i }))

    setCategories(reordered)

    try {
      await giftCategoriesApi.reorder(reordered.map(c => ({ id: c.id, sort_order: c.sort_order })))
      fetchCategories()
    } catch (e) {
      fetchCategories()
      alert((e as Error).message)
    }
  }

  const openEditCategory = (cat: Category) => {
    setEditingCategory(cat)
    setNewCategory(cat.name)
  }

  const getImage = (gift: GiftItem) => gift.image || gift.image_url || ''

  return (
    <div className="p-6 space-y-6">
      {/* 页面标题 */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">礼品管理</h1>
          <p className="text-gray-500 text-sm mt-1">共 {total} 件礼品</p>
        </div>
        <div className="flex items-center gap-3">
          {!isSupervisor && (
            <button onClick={() => { setNewCategory(''); setEditingCategory(null); setShowCategoryModal(true); fetchCategories() }}
              className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-xl hover:bg-gray-50 transition-colors">
              <FolderPlus size={18} /> 分类管理
            </button>
          )}
          <button onClick={openAdd}
            className="flex items-center gap-2 bg-green-700 text-white px-4 py-2 rounded-xl hover:bg-green-800 transition-colors">
            <Plus size={18} /> 添加礼品
          </button>
        </div>
      </div>

      {/* 筛选栏 */}
      <div className="bg-white rounded-2xl shadow-md p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input type="text" placeholder="搜索礼品名称..."
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setCategoryFilter('all')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${categoryFilter === 'all' ? 'bg-green-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              全部 ({allTotal})
            </button>
            {categories.map(cat => (
              <button key={cat.id} onClick={() => setCategoryFilter(cat.name)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${categoryFilter === cat.name ? 'bg-green-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {cat.name} {cat.count !== undefined ? `(${cat.count})` : ''}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 礼品列表 */}
      {loading ? (
        <div className="text-center py-20 text-gray-400">加载中...</div>
      ) : error ? (
        <div className="text-center py-20 text-red-500">{error}</div>
      ) : gifts.length === 0 ? (
        <div className="text-center py-20 text-gray-400">暂无礼品</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {gifts.map(gift => {
            const img = getImage(gift)
            return (
              <div key={gift.id} className={`bg-white rounded-2xl shadow-md overflow-hidden ${gift.is_active ? '' : 'opacity-60'}`}>
                {/* 缩略图 */}
                <div className="relative aspect-square bg-gray-100">
                  {img ? (
                    <img src={img} alt={gift.name} className="w-full h-full object-cover"
                      onError={e => { e.currentTarget.style.display = 'none'; (e.currentTarget.nextElementSibling as HTMLElement)?.classList.remove('hidden') }} />
                  ) : null}
                  <div className={`absolute inset-0 flex items-center justify-center ${img ? 'hidden' : ''}`}>
                    <ImageIcon className="w-16 h-16 text-gray-300" />
                  </div>
                  <button onClick={() => handleToggle(gift)}
                    className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors">
                    {gift.is_active
                      ? <ToggleRight className="w-6 h-6 text-green-600" />
                      : <ToggleLeft className="w-6 h-6 text-gray-300" />}
                  </button>
                  <div className="absolute top-3 left-3 flex flex-col gap-1">
                    {!gift.creator_hospital_id && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">总后台添加</span>
                    )}
                    {gift.creator_hospital_id && isSupervisor && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">本机构创建</span>
                    )}
                    {gift.creator_hospital_id && !isSupervisor && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">机构创建</span>
                    )}
                    {gift.delivery_type === 'express' && (
                      <span className="px-3 py-1 bg-blue-500 text-white rounded-full text-xs font-medium flex items-center">
                        <Truck className="w-3 h-3 mr-1" /> 快递
                      </span>
                    )}
                    {gift.delivery_type === 'selfpickup' && (
                      <span className="px-3 py-1 bg-green-500 text-white rounded-full text-xs font-medium flex items-center">
                        <MapPin className="w-3 h-3 mr-1" /> 自提
                      </span>
                    )}
                    {gift.delivery_type === 'both' && (
                      <span className="px-3 py-1 bg-purple-500 text-white rounded-full text-xs font-medium">快递/自提</span>
                    )}
                  </div>
                  {/* 机构负责人查看总后台礼品：隐藏切换按钮 */}
                  {!(isSupervisor && !gift.creator_hospital_id) && (
                    <button onClick={() => handleToggle(gift)}
                      className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors">
                      {gift.is_active
                        ? <ToggleRight className="w-6 h-6 text-green-600" />
                        : <ToggleLeft className="w-6 h-6 text-gray-300" />}
                    </button>
                  )}
                </div>

                {/* 内容区域 */}
                <div className="p-4">
                  <h3 className="font-bold text-gray-800 mb-2">{gift.name}</h3>
                  <div className="flex items-center gap-1 mb-3">
                    <Tag className="w-4 h-4 text-orange-500" />
                    <span className="text-lg font-bold text-orange-500">{gift.points_required}</span>
                    <span className="text-sm text-gray-500">积分</span>
                    {gift.exchange_limit !== undefined && gift.exchange_limit > 0 && (
                      <span className="ml-1 text-xs text-gray-400">限兑{gift.exchange_limit}次</span>
                    )}
                  </div>
                  {gift.price > 0 && (
                    <div className="text-xs text-gray-400 mb-2">市场价: ¥{gift.price}</div>
                  )}
                  <div className="flex items-center gap-1 mb-2">
                    <span className={`text-sm font-medium ${gift.stock <= 5 ? 'text-red-500' : 'text-green-600'}`}>
                      库存: {gift.stock}件
                    </span>
                    {gift.stock <= 5 && gift.stock > 0 && (
                      <span className="text-xs text-red-500 bg-red-50 px-2 py-0.5 rounded-full">库存紧张</span>
                    )}
                    {gift.stock === 0 && (
                      <span className="text-xs text-white bg-red-500 px-2 py-0.5 rounded-full">已售罄</span>
                    )}
                  </div>
                  {gift.hospital_names && gift.hospital_names.length > 0 && (
                    <div className="text-xs text-gray-400 mb-2">
                      关联: {gift.hospital_names.join('、')}
                    </div>
                  )}
                  <div className="flex gap-2 pt-3 border-t">
                    {isSupervisor && !gift.creator_hospital_id ? (
                      // 机构负责人看总后台礼品：只读
                      <button disabled className="flex-1 flex items-center justify-center gap-1 py-2 text-gray-300 rounded-lg cursor-not-allowed">
                        <Edit size={14} />查看
                      </button>
                    ) : (
                      <>
                        <button onClick={() => openEdit(gift)}
                          className="flex-1 flex items-center justify-center gap-1 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                          <Edit size={14} />编辑
                        </button>
                        <button onClick={() => setDeletingGift(gift)}
                          className="flex-1 flex items-center justify-center gap-1 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                          <Trash2 size={14} />删除
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* 分页 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">第 {currentPage} / {totalPages} 页，共 {total} 条</span>
          <div className="flex gap-2">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border rounded-lg disabled:opacity-40 hover:bg-gray-50">
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(currentPage - 2, totalPages - 4)) + i
              return p <= totalPages ? (
                <button key={p} onClick={() => setCurrentPage(p)}
                  className={`px-3 py-1.5 border rounded-lg ${p === currentPage ? 'bg-green-600 text-white' : 'hover:bg-gray-50'}`}>
                  {p}
                </button>
              ) : null
            })}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 border rounded-lg disabled:opacity-40 hover:bg-gray-50">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* 添加/编辑弹窗 */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-lg mx-4 shadow-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
              <h3 className="text-lg font-bold text-gray-800">
                {modalType === 'add' ? '添加礼品' : '编辑礼品'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* 图片上传 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">礼品图片</label>
                <label className={`border-2 border-dashed border-gray-200 rounded-xl text-center cursor-pointer hover:border-green-500 transition-colors block ${imagePreview ? '' : 'py-8'}`}>
                  {imagePreview ? (
                    <div className="relative inline-block">
                      <img src={imagePreview} alt="" className="w-full aspect-square object-cover rounded-lg max-w-[200px]" />
                      <span className="absolute -bottom-2 -right-2 px-2 py-1 bg-green-600 text-white text-xs rounded-full">点击更换</span>
                    </div>
                  ) : (
                    <>
                      <ImageIcon className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">点击上传图片，建议尺寸 200x200</p>
                    </>
                  )}
                  <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                </label>
                {uploadingImage && (
                  <p className="text-sm text-green-600 mt-2">图片上传中...</p>
                )}
                {imageFile && !uploadingImage && (
                  <p className="text-sm text-gray-500 mt-2">已选择图片，正在上传...</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">礼品名称 <span className="text-red-500">*</span></label>
                <input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="请输入礼品名称"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">所属分类</label>
                <select value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 bg-white">
                  <option value="">请选择分类</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">详细介绍</label>
                <RichEditor value={descContent} onChange={setDescContent} placeholder="请输入礼品详细介绍..." />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">所需积分 <span className="text-red-500">*</span></label>
                  <input type="number" value={formData.points_required} onChange={e => setFormData({ ...formData, points_required: e.target.value })}
                    placeholder="请输入积分"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">市场价(元)</label>
                  <input type="number" value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })}
                    placeholder="请输入市场价格"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">库存数量 <span className="text-red-500">*</span></label>
                  <input type="number" value={formData.stock} onChange={e => setFormData({ ...formData, stock: e.target.value })}
                    placeholder="请输入库存"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">每人兑换上限</label>
                  <input type="number" value={formData.exchange_limit} onChange={e => setFormData({ ...formData, exchange_limit: e.target.value })}
                    placeholder="0表示不限制"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">排序权重</label>
                <input type="number" value={formData.sort_order} onChange={e => setFormData({ ...formData, sort_order: e.target.value })}
                  placeholder="数字越大越靠前"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>

              {/* 配送方式 Radio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">配送方式</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'express', label: '快递配送', Icon: Truck },
                    { value: 'selfpickup', label: '线下自提', Icon: MapPin },
                    { value: 'both', label: '两者均可', Icon: null },
                  ].map(({ value, label, Icon }) => (
                    <label key={value} className={`flex items-center justify-center p-3 border-2 rounded-xl cursor-pointer transition-colors ${formData.delivery_type === value ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-green-400'}`}>
                      <input type="radio" name="deliveryType" value={value} checked={formData.delivery_type === value}
                        onChange={e => setFormData({ ...formData, delivery_type: e.target.value })}
                        className="sr-only" />
                      {Icon && <Icon className="w-5 h-5 text-gray-500 mr-2" />}
                      <span className="text-sm font-medium text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* 启用开关 */}

              {/* 关联机构（多选）- supervisor 添加时隐藏 */}
              {!isSupervisor && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">关联机构（可多选）</label>
                  <div className="border border-gray-200 rounded-xl p-3 max-h-40 overflow-y-auto space-y-2">
                    {hospitals.length === 0 && <p className="text-sm text-gray-400">暂无可选机构</p>}
                    {hospitals.map(h => {
                      const checked = formData.hospital_ids.includes(h.id)
                      return (
                        <label key={h.id} className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={checked}
                            onChange={e => {
                              const ids = e.target.checked
                                ? [...formData.hospital_ids, h.id]
                                : formData.hospital_ids.filter(id => id !== h.id)
                              setFormData({ ...formData, hospital_ids: ids })
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-green-600" />
                          <span className="text-sm text-gray-700">{h.name}</span>
                        </label>
                      )
                    })}
                  </div>
                  {formData.hospital_ids.length > 0 && (
                    <p className="text-xs text-gray-400 mt-1">已选择 {formData.hospital_ids.length} 个机构</p>
                  )}
                </div>
              )}
              {isSupervisor && (
                <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                  <Shield size={14} className="text-green-600" />
                  <span>本机构创建，绑定机构：<strong className="text-gray-700">{authUser?.hospitalName || '本机构'}</strong></span>
                </div>
              )}
              <div className="flex items-center gap-3">
                <button onClick={() => setIsActive(!isActive)}
                  className={`w-12 h-6 rounded-full transition-colors ${isActive ? 'bg-green-600' : 'bg-gray-300'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${isActive ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
                <span className="text-sm text-gray-600">上架该礼品</span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t shrink-0">
              <button onClick={() => setShowModal(false)} className="px-6 py-2 border border-gray-200 rounded-xl hover:bg-gray-50">取消</button>
              <button onClick={handleSubmit} disabled={formLoading}
                className="px-6 py-2 bg-green-700 text-white rounded-xl hover:bg-green-800 disabled:opacity-50">
                {formLoading ? '提交中...' : modalType === 'add' ? '添加' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 删除确认 */}
      {deletingGift && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-bold mb-2">确认删除</h3>
            <p className="text-gray-500 text-sm mb-6">
              确定要删除礼品 <strong>{deletingGift.name}</strong> 吗？删除后无法恢复。
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeletingGift(null)} className="flex-1 py-2 border rounded-xl hover:bg-gray-50">取消</button>
              <button onClick={handleDelete} className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl">确认删除</button>
            </div>
          </div>
        </div>
      )}

      {/* 分类管理弹窗 */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-bold text-gray-800 flex items-center">
                <Folder className="w-5 h-5 mr-2" /> 分类管理
              </h3>
              <button onClick={() => { setShowCategoryModal(false); setNewCategory(''); setEditingCategory(null) }}
                className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 space-y-4">
              {/* 添加新分类 */}
              <div className="flex items-center gap-3">
                <input type="text" placeholder="输入新分类名称"
                  value={newCategory} onChange={e => setNewCategory(e.target.value)}
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                <button onClick={editingCategory ? handleEditCategory : handleAddCategory}
                  className="px-6 py-3 bg-green-700 text-white rounded-xl hover:bg-green-800">
                  {editingCategory ? '保存' : '添加'}
                </button>
              </div>

              {/* 分类列表 */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">已有分类 ({categories.length}) <span className="text-xs text-gray-400">(数值越大越靠前)</span></p>
                {[...categories].sort((a, b) => b.sort_order - a.sort_order).map(cat => (
                  <div key={cat.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono bg-green-100 text-green-700 px-1.5 py-0.5 rounded">{cat.sort_order}</span>
                      <Folder className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-gray-800">{cat.name}</span>
                      <span className="text-sm text-gray-400">({cat.count ?? 0}个礼品)</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleMoveCategory(cat.id, 'up')}
                        className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors" title="上移">
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleMoveCategory(cat.id, 'down')}
                        className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors" title="下移">
                        <ArrowDown className="w-4 h-4" />
                      </button>
                      <button onClick={() => openEditCategory(cat)}
                        className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeleteCategory(cat.id)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end px-6 py-4 border-t">
              <button onClick={() => { setShowCategoryModal(false); setNewCategory(''); setEditingCategory(null) }}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200">
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Gifts
